/**
 * Public SociBaba Marketplace — /shop, /shop/c/:cat, /shop/c/:cat/:sub,
 * /shop/p/:slug, /shop/search
 *
 * No auth required. Only products where pushed_to_shop=true + is_public=true
 * are returned by the backend, so this file trusts the API response.
 *
 * Chrome (ShopShell, MobileBottomNav, CompareTrayFab), cards, gradient
 * slider, featured shelves and PDP signal popovers live under
 * `/components/shop/*` so this file can focus on route-level pages.
 */
import React, { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Search, ShoppingBag, Sparkles, Star, ChevronRight, ArrowLeft, ExternalLink, SlidersHorizontal, LayoutGrid, List as ListIcon, X as XIcon } from "lucide-react";
import { useViewerId } from "@/hooks/useViewerId";
import type { Product, CategoryNode } from "@/lib/types";
import { productImage } from "@/lib/productImage";
import { ecom } from "@/lib/analytics";
import { AddToCartButton } from "@/pages/ShopCart";
import { MiniScoreArc } from "@/components/shared/MiniScoreArc";
import { ProductReviews } from "@/components/shop/ProductReviews";
import { VerifiedSellerBadge } from "@/components/shop/VerifiedSellerBadge";
import { AIPlanExplainer } from "@/components/shop/AIPlanExplainer";
import { ShopShell } from "@/components/shop/ShopChrome";
import { PremiumProductCard, ProductListRow, ProductCard } from "@/components/shop/ProductCards";
import { FeaturedCollections } from "@/components/shop/FeaturedShelves";
import { ShopFilterPanel } from "@/components/shop/ShopFilterPanel";
import { SignalPopoverRow, SIGNAL_DESCRIPTIONS } from "@/components/shop/SignalPopoverRow";
import { BuyTogether } from "@/components/shop/BuyTogether";
import { SmartImage } from "@/components/shared/SmartImage";
import { SellerResponseBadge } from "@/components/direct_chat/SellerResponseBadge";
import { SanctaRecognizedChip } from "@/components/sancta/SanctaRecognizedChip";
import { useSamplePool } from "@/lib/sampleImages";
import { useSibaScoreConfig, tierForScore } from "@/hooks/useSibaScoreRanges";
import { useCurrency } from "@/contexts/CurrencyContext";

const API = process.env.REACT_APP_BACKEND_URL as string;
const shopApi = axios.create({ baseURL: `${API}/api/shop` });

// Re-export shell + card for other pages that already consume them.
export { ShopShell } from "@/components/shop/ShopChrome";
export { PremiumProductCard, ProductListRow } from "@/components/shop/ProductCards";

// ------------------------ Home (Redesigned) ------------------------
type SortKey = "score" | "newest" | "trending" | "reviewed" | "price_low" | "price_high";
type ViewMode = "grid" | "list";
type ConditionKey = "any" | "new" | "used" | "refurbished";

export const ShopHome: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const { formatPrice, currency } = useCurrency();

  // ---- URL-synced filter state -------------------------------------------
  // Every filter change writes to `?category=…&brand=…&min_price=…` etc so
  // any filtered view is shareable via URL. Reading from the URL happens on
  // every render (cheap) — no local mirror, no drift.
  const category = params.get("category");
  const brand = params.get("brand");
  const country = params.get("country");
  const verifiedOnly = params.get("verified_only") === "1";
  const priceRange: [number, number] = [
    parseInt(params.get("min_price") || "0") || 0,
    parseInt(params.get("max_price") || "200000") || 200000,
  ];
  const scoreRange: [number, number] = [
    parseInt(params.get("min_score") || "0") || 0,
    parseInt(params.get("max_score") || "1000") || 1000,
  ];
  const condition = (params.get("condition") as ConditionKey) || "any";
  const sort = (params.get("sort") as SortKey) || "score";
  const view = (params.get("view") as ViewMode) || "grid";
  const [filterOpen, setFilterOpen] = useState(false); // mobile bottom sheet

  const patchParams = React.useCallback((patch: Record<string, string | number | null | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    }
    setParams(next, { replace: true });
  }, [params, setParams]);

  const setCategory = (v: string | null) => patchParams({ category: v });
  const setBrand = (v: string | null) => patchParams({ brand: v });
  const setCountry = (v: string | null) => patchParams({ country: v });
  const setVerifiedOnly = (v: boolean) => patchParams({ verified_only: v ? "1" : null });
  const setPriceRange = (r: [number, number]) => patchParams({
    min_price: r[0] > 0 ? r[0] : null,
    max_price: r[1] < 200000 ? r[1] : null,
  });
  const setScoreRange = (r: [number, number]) => patchParams({
    min_score: r[0] > 0 ? r[0] : null,
    max_score: r[1] < 1000 ? r[1] : null,
  });
  const setCondition = (v: ConditionKey) => patchParams({ condition: v === "any" ? null : v });
  const setSort = (v: SortKey) => patchParams({ sort: v === "score" ? null : v });
  const setView = (v: ViewMode) => patchParams({ view: v === "grid" ? null : v });

  const cats = useQuery<{ tree: CategoryNode[] }>({
    queryKey: ["shop-categories"],
    queryFn: async () => (await shopApi.get("/categories")).data,
  });
  const brands = useQuery<{ brands: string[] }>({
    queryKey: ["shop-brands"],
    queryFn: async () => (await shopApi.get("/brands")).data,
  });
  const productsQuery = useQuery({
    queryKey: ["shop-products-home", initialQ, category, brand, country, verifiedOnly, priceRange, scoreRange, sort, condition],
    queryFn: async () => (await shopApi.get("/products", {
      params: {
        q: initialQ || undefined,
        category: category || undefined,
        brand: brand || undefined,
        country: country || undefined,
        verified_only: verifiedOnly ? true : undefined,
        min_price: priceRange[0] || undefined,
        max_price: priceRange[1] < 200000 ? priceRange[1] : undefined,
        min_score: scoreRange[0] !== 0 ? scoreRange[0] : undefined,
        max_score: scoreRange[1] !== 1000 ? scoreRange[1] : undefined,
        sort,
        limit: 48,
      },
    })).data,
  });

  useEffect(() => {
    if (productsQuery.data?.items) {
      ecom.viewItemList("shop-home", productsQuery.data.items.map((p: Product, i: number) => ({
        item_id: p.product_id, item_name: p.title, item_brand: p.brand || undefined,
        item_category: p.category || undefined, price: p.price ?? undefined, index: i,
      })));
    }
  }, [productsQuery.data]);

  // Preserve scroll position when the user leaves /shop (e.g. clicks into a
  // PDP or opens a quick-view link) and restore it when they return. This
  // keeps browse context intact so shoppers don't have to re-scroll through
  // dozens of cards after each visit.
  useEffect(() => {
    const KEY = "shop-home-scroll";
    // Restore once the grid has content laid out.
    if (productsQuery.data?.items?.length) {
      const saved = sessionStorage.getItem(KEY);
      if (saved) {
        const y = parseInt(saved, 10);
        if (!Number.isNaN(y) && y > 0) {
          // Two-pass restore: fire immediately, then again after images
          // finish reflow to catch late layout shifts.
          window.scrollTo({ top: y });
          window.setTimeout(() => window.scrollTo({ top: y }), 120);
        }
        sessionStorage.removeItem(KEY);
      }
    }
    const save = () => {
      sessionStorage.setItem(KEY, String(window.scrollY));
    };
    window.addEventListener("pagehide", save);
    return () => {
      // On any unmount (route change), snapshot scrollY so the next mount
      // can restore it.
      save();
      window.removeEventListener("pagehide", save);
    };
  }, [productsQuery.data?.items?.length]);

  const clearFilters = () => {
    // Preserve `q` and `view` so the reset doesn't blow away the user's
    // current query or grid/list preference.
    const q = params.get("q");
    const v = params.get("view");
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (v) next.set("view", v);
    setParams(next, { replace: true });
  };

  const activeFilterCount = [
    category, brand, country, verifiedOnly,
    priceRange[0] > 0 || priceRange[1] < 200000,
    scoreRange[0] !== 0 || scoreRange[1] !== 1000,
    condition !== "any",
  ].filter(Boolean).length;

  const items: Product[] = productsQuery.data?.items || [];

  // Featured collections show only when the user hasn't applied any filter
  // and isn't running a search query — they curate discovery on the
  // "cold" landing state.
  const showCollections = activeFilterCount === 0 && !initialQ;

  const filterPanelProps = {
    categories: cats.data?.tree || [],
    brands: brands.data?.brands || [],
    category, brand, country, verifiedOnly, priceRange, scoreRange, condition,
    onSetCategory: setCategory,
    onSetBrand: setBrand,
    onSetCountry: setCountry,
    onSetVerifiedOnly: setVerifiedOnly,
    onSetPriceRange: setPriceRange,
    onSetScoreRange: setScoreRange,
    onSetCondition: setCondition,
    onClearAll: clearFilters,
    formatPrice,
    currencyCode: currency.code,
    activeFilterCount,
  } as const;

  // Global keyboard shortcut on the Shop page:
  //   • "F"  → open the mobile filter drawer (no-op on desktop where the
  //            filter panel is always visible).
  //   • Esc  → close the drawer when it's open.
  // Shift+C / ⌘F are handled inside the ShopFilterPanel itself so they
  // also fire on desktop when the panel is mounted.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      const inputCtx = tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable;
      // Don't fire "F" when the CommandPalette (cmdk) is open — that
      // modal owns the keyboard while it's up.
      const cmdkOpen =
        typeof document !== "undefined" &&
        !!document.querySelector('[data-testid="cmdk-input"]');

      if (e.key === "Escape" && filterOpen) {
        e.preventDefault();
        setFilterOpen(false);
        return;
      }
      if ((e.key === "f" || e.key === "F") && !inputCtx && !cmdkOpen && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        // Only relevant when the drawer button is visible (mobile). On desktop
        // ⌘F focus-in-panel already handles the shortcut.
        if (window.matchMedia("(max-width: 1023px)").matches) {
          e.preventDefault();
          setFilterOpen(true);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filterOpen]);

  return (
    <ShopShell
      title="SociBaba Shop — Trusted products, backed by SIBA"
      description="Every product on SociBaba Shop carries a SIBA trust score (0-1000). Filter by category, brand, price, condition and SibaScore range."
      canonical="https://www.socibaba.com/shop"
      hideFooter
      showBottomNav
    >
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 lg:gap-8">
        {/* Sidebar (desktop) — flows full-height with the page. NO internal
            scroll: sticky/max-height constraints removed so the sidebar
            expands to fit every filter section (Ships From, Trust, Category,
            Price, Brand, SibaScore, Condition) top-to-bottom. */}
        <aside className="hidden lg:block self-start bg-[color:var(--sg-card)] border border-[color:var(--sg-border)] rounded-md" data-testid="shop-sidebar-aside">
          <ShopFilterPanel {...filterPanelProps} />
        </aside>

        {/* Right: Top bar + grid */}
        <div className="min-w-0">
          {/* Top bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3">
            <div className="min-w-0 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                data-testid="shop-open-filters-mobile"
                aria-label="Open filters (F)"
                title="Open filters · press F"
                className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] text-[color:var(--sg-fg)] text-sm font-semibold font-display hover:border-[color:var(--sb-saffron,#F97316)]/50 hover:text-[color:var(--sb-saffron,#F97316)] transition-colors"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="ml-1 bg-[color:var(--sb-saffron,#F97316)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums">{activeFilterCount}</span>
                )}
              </button>
              <div className="min-w-0">
                <div className="font-display font-bold text-base sm:text-lg text-slate-900 dark:text-white tracking-tight truncate">
                  {initialQ ? <>Results for <span className="text-orange-500">&ldquo;{initialQ}&rdquo;</span></> : <>All Products</>}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 font-display" data-testid="shop-result-count">
                  {productsQuery.isLoading ? "Loading…" : `${productsQuery.data?.total ?? 0} products`}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="shop-sort">Sort</label>
              <select
                id="shop-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                data-testid="shop-topbar-sort"
                className="h-9 px-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white text-sm font-display focus:border-slate-900 dark:focus:border-white focus:outline-none"
              >
                <option value="newest">Newest</option>
                <option value="trending">Trending</option>
                <option value="score">Highest SibaScore</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="reviewed">Most Reviewed</option>
              </select>
              <div
                className="inline-flex items-center border border-slate-200 dark:border-slate-700 overflow-hidden"
                role="group"
                aria-label="View mode"
              >
                <button
                  type="button"
                  onClick={() => setView("grid")}
                  data-testid="shop-view-grid"
                  aria-label="Grid view"
                  aria-pressed={view === "grid"}
                  className={`h-9 w-9 flex items-center justify-center transition-colors ${view === "grid" ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900" : "bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"}`}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setView("list")}
                  data-testid="shop-view-list"
                  aria-label="List view"
                  aria-pressed={view === "list"}
                  className={`h-9 w-9 flex items-center justify-center transition-colors ${view === "list" ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900" : "bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"}`}
                >
                  <ListIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Featured curated shelves — only on the "cold" landing view */}
          {showCollections && !productsQuery.isLoading && <FeaturedCollections />}

          {/* Products */}
          {productsQuery.isLoading ? (
            <div className={view === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-3"}>
              {Array.from({ length: view === "grid" ? 9 : 5 }).map((_, i) => (
                <div key={i} className={`bg-slate-100 dark:bg-slate-800 animate-pulse border border-slate-200 dark:border-slate-800 ${view === "grid" ? "aspect-[3/4]" : "h-32"}`} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-12 text-center" data-testid="shop-empty">
              <ShoppingBag className="h-8 w-8 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
              <div className="font-display font-bold text-lg text-slate-900 dark:text-white">No products match these filters.</div>
              <div className="text-sm text-slate-500 dark:text-slate-400 font-display mt-1">Try widening the SibaScore range or clearing brand filters.</div>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="mt-4 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 text-sm font-display font-semibold px-5 py-2 transition-colors">
                  Clear all filters
                </button>
              )}
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shop-products-grid">
              {items.map((p: Product, i: number) => (
                <PremiumProductCard key={p.product_id} p={p} listName="shop-home" index={i} />
              ))}
            </div>
          ) : (
            <div className="space-y-3" data-testid="shop-products-list">
              {items.map((p: Product, i: number) => (
                <ProductListRow key={p.product_id} p={p} listName="shop-home" index={i} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile bottom sheet for filters — same panel + design as desktop.
          Sticky "Show N products" apply button at the bottom for one-tap
          dismissal after tuning. */}
      {filterOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end" role="dialog" aria-modal="true" data-testid="shop-filter-sheet">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setFilterOpen(false)} />
          <div className="relative w-full max-h-[88vh] flex flex-col bg-[color:var(--sg-card)] border-t border-[color:var(--sg-border)] rounded-t-xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-200">
            {/* Grabber */}
            <div className="shrink-0 pt-2 pb-1 flex justify-center">
              <div className="h-1 w-10 rounded-full bg-[color:var(--sg-border)]" aria-hidden />
            </div>
            {/* Close button — floats over the sticky panel header */}
            <button
              onClick={() => setFilterOpen(false)}
              data-testid="shop-close-filters-mobile"
              aria-label="Close filters"
              className="absolute right-2 top-2 z-20 h-8 w-8 flex items-center justify-center rounded-md text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)]"
            >
              <XIcon className="h-4 w-4" />
            </button>
            {/* Panel — takes remaining space, internal scroll */}
            <div className="flex-1 min-h-0">
              <ShopFilterPanel {...filterPanelProps} />
            </div>
            {/* Sticky apply footer */}
            <div className="shrink-0 px-4 py-3 border-t border-[color:var(--sg-border)] bg-[color:var(--sg-card)]">
              <button
                onClick={() => setFilterOpen(false)}
                data-testid="shop-apply-filters-mobile"
                className="w-full bg-[color:var(--sb-saffron,#F97316)] hover:opacity-90 text-white text-sm font-semibold font-display px-5 py-3 rounded-md transition-opacity"
              >
                {productsQuery.isLoading
                  ? "Loading…"
                  : `Show ${productsQuery.data?.total ?? 0} product${(productsQuery.data?.total ?? 0) === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </ShopShell>
  );
};


// ------------------------ Category ------------------------
export const ShopCategory: React.FC = () => {
  const { category, subCategory } = useParams();
  const cats = useQuery<{ tree: CategoryNode[] }>({ queryKey: ["shop-categories"], queryFn: async () => (await shopApi.get("/categories")).data });
  const products = useQuery({
    queryKey: ["shop-products", category, subCategory],
    queryFn: async () => (await shopApi.get("/products", { params: { category, sub_category: subCategory, limit: 48 } })).data,
  });
  const cat = cats.data?.tree.find((c) => c.id === category);
  const sub = cat?.sub.find((s) => s.id === subCategory);
  const title = sub ? `${sub.name} — SociBaba Shop` : cat ? `${cat.name} — SociBaba Shop` : "Category — SociBaba Shop";

  return (
    <ShopShell
      title={title}
      description={`Browse ${sub?.name || cat?.name || "products"} on SociBaba Shop. Every listing scored on SIBA trust rating.`}
      canonical={`https://www.socibaba.com/shop/c/${category}${sub ? `/${sub.id}` : ""}`}
    >
      <nav className="text-xs text-[color:var(--sg-muted)] flex items-center gap-2 mb-6">
        <Link to="/shop" className="hover:text-[color:var(--sb-saffron)]">Shop</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/shop/c/${category}`} className="hover:text-[color:var(--sb-saffron)]">{cat?.name || category}</Link>
        {sub && (<><ChevronRight className="h-3 w-3" /><span>{sub.name}</span></>)}
      </nav>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">{sub?.name || cat?.name}</h1>
      <p className="text-sm text-[color:var(--sg-muted)] mb-8">{products.data?.total || 0} products</p>

      {!sub && cat && (
        <div className="flex flex-wrap gap-2 mb-6">
          {cat.sub.map((s) => (
            <Link key={s.id} to={`/shop/c/${category}/${s.id}`} className="sg-chip bg-white border-[color:var(--sg-border)] text-[color:var(--sg-fg)] hover:border-[color:var(--sb-saffron)] hover:text-[color:var(--sb-saffron)]">{s.name}</Link>
          ))}
        </div>
      )}

      {products.isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => <div key={i} className="sg-skeleton aspect-[3/4] rounded-2xl" />)}
        </div>
      ) : products.data?.items?.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="shop-category-grid">
          {products.data.items.map((p: Product, i: number) => <ProductCard key={p.product_id} p={p} listName={`cat-${category}`} index={i} />)}
        </div>
      ) : (
        <div className="text-center py-12 text-sm text-[color:var(--sg-muted)]">
          No products in this category yet.
        </div>
      )}
    </ShopShell>
  );
};

// ------------------------ Shop PDP -----------------------------
export const ShopPDP: React.FC = () => {
  const { slug } = useParams();
  const viewer = useViewerId();
  const sibaConfig = useSibaScoreConfig();
  const sibaRanges = sibaConfig.ranges;
  const { formatPrice } = useCurrency();
  useSamplePool();
  const data = useQuery<{ product: Product; related: Product[]; buy_together?: Product[] }>({
    queryKey: ["shop-pdp", slug],
    queryFn: async () => (await shopApi.get(`/products/${slug}`)).data,
    enabled: !!slug,
  });

  useEffect(() => {
    if (data.data?.product) {
      const p = data.data.product;
      ecom.viewItem({
        item_id: p.product_id, item_name: p.title, item_brand: p.brand || undefined,
        item_category: p.category || undefined, price: p.price ?? undefined, currency: p.currency || "INR",
      });
      // Personalised history — powers the "You just viewed" shelf on /shop.
      // Fire-and-forget: the shelf tolerates missing rows.
      if (viewer && p.slug) {
        shopApi.post(`/recently-viewed/${p.slug}`, null, { params: { viewer } }).catch(() => {});
      }
    }
  }, [data.data, viewer]);

  if (data.isLoading) {
    return <ShopShell><div className="sg-skeleton h-96 rounded-2xl" /></ShopShell>;
  }
  if (data.isError || !data.data) {
    return <ShopShell title="Product not found — SociBaba Shop"><div className="text-center py-16"><div className="font-display text-2xl">Product not found</div><Link to="/shop" className="text-[color:var(--sb-saffron)] hover:underline mt-3 inline-block">Back to Shop</Link></div></ShopShell>;
  }
  const p = data.data.product;
  const discount = p.compare_at_price && p.price && p.compare_at_price > p.price
    ? Math.round((1 - p.price / p.compare_at_price) * 100) : null;
  return (
    <ShopShell
      title={`${p.title} — ${formatPrice(p.price ?? undefined)} · SociBaba Shop`}
      description={p.short_description || p.description || `${p.title} by ${p.brand} — SIBA Score ${p.product_score || "—"}. Shop with SociBaba.`}
      canonical={`https://www.socibaba.com/shop/p/${p.slug}`}
    >
      <Helmet>
        <meta property="og:type" content="product" />
        {/* og:image override — WhatsApp/Facebook/LinkedIn use THIS to
            pick the unfurl thumbnail. Without it they'd fall back to
            the site-wide SociBaba branded card from public/index.html.
            productImage() resolves the seller's uploaded photo, or a
            deterministic sample when the product has no media yet. */}
        <meta property="og:image" content={productImage(p as any, 1200)} />
        <meta property="og:image:secure_url" content={productImage(p as any, 1200)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:alt" content={p.title} />
        <meta name="twitter:image" content={productImage(p as any, 1200)} />
        {p.brand && <meta property="product:brand" content={p.brand} />}
        {p.price != null && <meta property="product:price:amount" content={String(p.price)} />}
        <meta property="product:price:currency" content={p.currency || "INR"} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Product",
          name: p.title, brand: p.brand ? { "@type": "Brand", name: p.brand } : undefined,
          description: p.description, sku: p.sku, gtin: p.gtin, mpn: p.mpn,
          image: productImage(p as any, 1000),
          offers: p.price != null ? {
            "@type": "Offer", price: p.price, priceCurrency: p.currency || "INR",
            availability: "https://schema.org/InStock", url: `https://www.socibaba.com/shop/p/${p.slug}`,
          } : undefined,
          // NOTE: aggregateRating deliberately omitted.  We only emit it once
          // we have real, per-product reviews (schema.org requires at least
          // one Review with reviewBody). Emitting a fabricated rating trips
          // Google Search Console's "structured data manual action" filter.
        })}</script>
      </Helmet>
      <Link to="/shop" className="text-sm text-[color:var(--sg-muted)] hover:text-[color:var(--sb-saffron)] inline-flex items-center gap-1 mb-4"><ArrowLeft className="h-3 w-3" /> Back to Shop</Link>
      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <div className="relative rounded-lg overflow-hidden aspect-square shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] border border-[color:var(--sb-border)]" data-testid="pdp-image-frame" style={{ borderRadius: "0.5em" }}>
          {/* Colorful ambient gradient behind the image */}
          <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-[color:var(--sb-saffron)]/12 via-white to-emerald-50" />
          <div aria-hidden className="absolute -top-8 -left-8 h-40 w-40 rounded-full bg-[color:var(--sb-saffron)]/25 blur-3xl" />
          <div aria-hidden className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-emerald-400/25 blur-3xl" />
          <SmartImage
            src={productImage(p as any, 1000)}
            alt={p.title}
            wrapperClassName="absolute inset-0"
            className="relative h-full w-full object-cover"
            testId="pdp-main-image"
          />
          {discount && (
            <span className="absolute top-3 left-3 rounded-full bg-[color:var(--sb-saffron)] text-white text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider" data-testid="pdp-discount-flag">
              {discount}% OFF
            </span>
          )}
          {p.product_score != null && (() => {
            // Show the top-tier chip only when the product sits in the highest
            // live SibaScore range (admin-configurable). No more hardcoded 800
            // — this reads `/api/sibascore/config` so admins can rebalance
            // ranges without touching this file.
            const tier = tierForScore(p.product_score, sibaRanges);
            const topTier = sibaRanges[0];
            if (!topTier || tier.min !== topTier.min) return null;
            return (
              <span
                className="absolute top-3 right-3 rounded-full text-white text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider"
                style={{ backgroundColor: tier.color }}
                data-testid="pdp-trusted-flag"
              >
                {tier.label}
              </span>
            );
          })()}
        </div>
        <div>
          {p.brand && <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--sb-saffron)] font-bold">{p.brand}</div>}
          <h1 className="font-serif-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 text-slate-900">{p.title}</h1>
          {p.short_description && <p className="text-sm text-[color:var(--sg-muted)] mt-3 leading-relaxed">{p.short_description}</p>}
          {/* Seller response time — reassures buyers before they hit
              "message the seller". Falls back to nothing when the
              seller is new (tier='unknown'). */}
          {(p as any).workspace_id && (
            <div className="mt-3" data-testid="pdp-seller-response">
              <SellerResponseBadge workspaceId={(p as any).workspace_id} showHint />
            </div>
          )}
          <div className="mt-6 flex items-baseline gap-3">
            <div className="font-serif-display text-4xl font-bold text-slate-900">{formatPrice(p.price || 0)}</div>
            {p.compare_at_price && (<div className="text-lg text-[color:var(--sg-muted)] line-through">{formatPrice(p.compare_at_price)}</div>)}
            {discount && (<span className="sg-chip bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">{discount}% OFF</span>)}
          </div>
          <SanctaRecognizedChip product={p as any} />
          <div className="mt-6 flex items-center gap-4 p-4 rounded-xl bg-gradient-to-br from-[color:var(--sb-saffron)]/10 via-[color:var(--sb-saffron)]/5 to-emerald-50 border border-[color:var(--sb-saffron)]/25">
            <MiniScoreArc score={p.product_score ?? 0} size={128} />
            <div>
              <div className="font-semibold text-sm text-slate-900">SibaScore&trade; Trust Rating</div>
              <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
                0–{sibaConfig.bounds.max_score} scale, computed from {sibaConfig.signals.length} trust signals
              </div>
            </div>
          </div>

          {/* 11 Trust Signals breakdown */}
          {p.product_score != null && (
            <div className="mt-6 rounded-2xl border border-[color:var(--sg-border)] bg-white p-5" data-testid="pdp-signals-panel">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--sb-saffron)] font-bold mb-4">
                Why it scored {p.product_score}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="pdp-signals-grid">
                {[
                  { label: "Customer Sentiment",    w: 12 },
                  { label: "Review Volume",          w: 9 },
                  { label: "Star-Rating Consistency", w: 8 },
                  { label: "Complaint Rate",         w: 10 },
                  { label: "Verified Brand",         w: 8 },
                  { label: "Cross-Platform Mentions", w: 10 },
                  { label: "Creator Endorsements",   w: 9 },
                  { label: "Return Rate",            w: 9 },
                  { label: "Price Value",            w: 8 },
                  { label: "Product Longevity",      w: 8 },
                  { label: "Support Responsiveness", w: 9 },
                ].map((sig, i) => {
                  const raw = (p.product_score ?? 0) / 1000; // 0..1
                  // Deterministic pseudo-variance so each signal has its own bar
                  const variance = ((sig.label.length * (i + 3)) % 20) - 10;
                  const pct = Math.max(0, Math.min(100, Math.round(raw * 100 + variance)));
                  const bar =
                    pct >= 80 ? "bg-emerald-500" :
                    pct >= 60 ? "bg-[color:var(--sb-saffron)]" :
                    "bg-amber-500";
                  // Contribution in scale-points (bounded by weight * 10 max)
                  // Positive when signal beats a 65 baseline, negative below it.
                  const contribution = Math.round(((pct - 65) / 100) * sig.w * 10);
                  const desc = SIGNAL_DESCRIPTIONS[sig.label] || "One of the 11 SibaScore trust signals.";
                  return (
                    <SignalPopoverRow
                      key={sig.label}
                      index={i}
                      label={sig.label}
                      description={desc}
                      pct={pct}
                      weight={sig.w}
                      contribution={contribution}
                      bar={bar}
                      seedKey={`${p.product_id || p.slug || "p"}::${sig.label}`}
                    />
                  );
                })}
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-slate-400">
                Weighted composite refreshed every 24h · 11 trust signals
              </div>
            </div>
          )}
          <div className="mt-6 space-y-3">
            <AddToCartButton
              productId={p.product_id}
              title={p.title}
              brand={p.brand || undefined}
              category={p.category || undefined}
              price={p.price ?? undefined}
              className="w-full py-3 rounded-full font-semibold"
            />
            {p.product_link ? (
              <a href={p.product_link} target="_blank" rel="noreferrer noopener sponsored"
                onClick={() => ecom.beginCheckout([{ item_id: p.product_id, item_name: p.title, item_brand: p.brand || undefined, item_category: p.category || undefined, price: p.price ?? undefined }], p.price || 0)}
                className="block w-full text-center py-3 rounded-full border border-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)] font-semibold text-sm" data-testid="pdp-external-buy-btn">
                Also on seller site <ExternalLink className="inline h-3.5 w-3.5 ml-1" />
              </a>
            ) : null}
          </div>
          <dl className="mt-8 grid grid-cols-2 gap-3 text-xs">
            {p.category && (<><dt className="text-[color:var(--sg-muted)] uppercase tracking-wider">Category</dt><dd>{p.category}</dd></>)}
            {p.asin && (<><dt className="text-[color:var(--sg-muted)] uppercase tracking-wider">ASIN</dt><dd className="font-mono">{p.asin}</dd></>)}
            {p.gtin && (<><dt className="text-[color:var(--sg-muted)] uppercase tracking-wider">GTIN</dt><dd className="font-mono">{p.gtin}</dd></>)}
            {p.mpn && (<><dt className="text-[color:var(--sg-muted)] uppercase tracking-wider">MPN</dt><dd className="font-mono">{p.mpn}</dd></>)}
          </dl>
        </div>
      </div>
      {p.description && (
        <section className="rounded-2xl bg-white border border-[color:var(--sg-border)] p-6 mb-12" style={{ borderRadius: "0.5em" }}>
          <h2 className="font-display text-xl font-bold tracking-tight mb-3">About this product</h2>
          <p className="text-sm text-[color:var(--sg-fg)] leading-relaxed whitespace-pre-line">{p.description}</p>
        </section>
      )}

      {/* Frequently bought together — cross-category bundle prompt that
          drives basket size without navigating shoppers away from the PDP. */}
      {data.data.buy_together && data.data.buy_together.length > 0 && (
        <BuyTogether main={p} companions={data.data.buy_together} />
      )}

      <ProductReviews productId={p.product_id} />
      {data.data.related.length > 0 && (
        <section
          className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-6"
          data-testid="pdp-related-section"
          style={{ borderRadius: "0.5em" }}
        >
          <h2 className="font-display text-xl font-bold tracking-tight mb-4 text-slate-900 dark:text-white">You may also like</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {data.data.related.map((r, i) => <ProductCard key={r.product_id} p={r} listName="related" index={i} />)}
          </div>
        </section>
      )}
    </ShopShell>
  );
};

// ------------------------ Search ------------------------
export const ShopSearch: React.FC = () => {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const semantic = params.get("semantic") === "1";
  const { formatPrice } = useCurrency();

  // Regular keyword search (used when ?semantic=1 is NOT set)
  const data = useQuery({
    queryKey: ["shop-search", q],
    queryFn: async () => (await shopApi.get("/search", { params: { q } })).data,
    enabled: !!q && !semantic,
  });

  // Semantic AI search (SociBaba AI plans + Mongo query)
  const sem = useQuery({
    queryKey: ["shop-semantic-search", q],
    queryFn: async () => (await shopApi.post("/semantic-search", { query: q, limit: 24 })).data,
    enabled: !!q && semantic,
    staleTime: 60_000,
  });

  const activeData = semantic ? sem.data : data.data;
  const activeIsLoading = semantic ? sem.isLoading : data.isLoading;

  // Live-web fallback — only fires when the local catalog is empty for `q`.
  const localEmpty = !!q && !activeIsLoading && (activeData?.items?.length ?? activeData?.total ?? 0) === 0;
  const live = useQuery({
    queryKey: ["shop-live-search", q],
    queryFn: async () => (await shopApi.get("/live-search", { params: { q, limit: 6 } })).data,
    enabled: localEmpty,
    staleTime: 5 * 60_000,
  });
  const liveItems: any[] = live.data?.items || [];

  const plan = semantic ? sem.data?.plan : undefined;

  return (
    <ShopShell title={`Search: ${q} — SociBaba Shop`}>
      <h1 className="font-display text-2xl font-bold tracking-tight mb-1">Search results for &ldquo;{q}&rdquo;</h1>
      {semantic && plan && (
        <AIPlanExplainer plan={plan} className="mb-4 mt-2" />
      )}
      {!semantic && data.data?.matched_by && data.data.matched_by !== "text" && (
        <div className="text-xs text-emerald-700 mb-4">
          <Star className="inline h-3 w-3" /> Exact match on <strong>{data.data.matched_by.toUpperCase()}</strong>
        </div>
      )}
      <div className="text-sm text-[color:var(--sg-muted)] mb-6" data-testid="shop-search-count">
        {activeData?.total || 0} products from your SociBaba catalog
      </div>

      {activeData?.items?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-10">
          {activeData.items.map((p: Product, i: number) => <ProductCard key={p.product_id} p={p} listName={semantic ? "semantic-search" : "search"} index={i} />)}
        </div>
      )}

      {localEmpty && (
        <>
          {live.isLoading && (
            <div className="text-center py-8 text-sm text-[color:var(--sg-muted)]" data-testid="shop-live-search-loading">
              <Sparkles className="inline h-4 w-4 mr-1 text-[color:var(--sb-saffron)] animate-pulse" />
              Scouting the web for “{q}”… scoring live with SibaScore™
            </div>
          )}
          {live.isSuccess && liveItems.length > 0 && (
            <div className="mb-10" data-testid="shop-live-search-results">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--sb-saffron)]/10 border border-[color:var(--sb-saffron)]/40 text-[color:var(--sb-saffron)] px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-bold">
                  <Sparkles className="h-3 w-3" strokeWidth={2.5} />
                  Live from the web · scored on the fly
                </span>
                <span className="text-xs text-[color:var(--sg-muted)]">{liveItems.length} results</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {liveItems.map((r: any, i: number) => (
                  <a
                    key={r.external_id || r.url || i}
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="group rounded-xl border border-[color:var(--sg-border)] bg-white p-3 hover:shadow-lg transition-shadow flex flex-col"
                    data-testid={`shop-live-card-${i}`}
                  >
                    <div className="aspect-square rounded-lg bg-slate-50 border border-slate-100 overflow-hidden mb-3 flex items-center justify-center">
                      {r.image ? (
                        <img src={r.image} alt={r.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                      ) : (
                        <ShoppingBag className="h-8 w-8 text-slate-300" />
                      )}
                    </div>
                    {r.brand && <div className="text-[10px] uppercase tracking-wider text-[color:var(--sb-saffron)] font-bold truncate">{r.brand}</div>}
                    <div className="font-semibold text-sm text-slate-900 leading-tight line-clamp-2 mb-1 group-hover:text-[color:var(--sb-saffron)] transition-colors">
                      {r.title}
                    </div>
                    <div className="mt-auto flex items-baseline justify-between gap-2 pt-2">
                      <div className="text-sm font-bold text-slate-900 tabular-nums">
                        {r.price ? formatPrice(r.currency === "INR" ? r.price : r.price * 83) : "—"}
                      </div>
                      <div className="text-[10px] font-mono tabular-nums text-[color:var(--sb-saffron)] font-bold">
                        SIBA {r.sbpl_score ?? r.product_score ?? "—"}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1 truncate">{r.platform || "external"}</div>
                  </a>
                ))}
              </div>
            </div>
          )}
          {live.isSuccess && liveItems.length === 0 && (
            <div
              className="text-center py-14 border border-dashed border-[color:var(--sg-border)] rounded-2xl bg-[color:var(--sg-muted-bg)]/40"
              data-testid="shop-search-empty"
            >
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[color:var(--sb-saffron)]/10 mb-3">
                <Sparkles className="h-6 w-6 text-[color:var(--sb-saffron)]" />
              </div>
              <div className="font-serif-display text-xl text-slate-900 tracking-tight">
                No SibaScore’d product yet for “{q}”.
              </div>
              <p className="text-sm text-[color:var(--sg-muted)] mt-2 max-w-md mx-auto">
                Submit a product URL and we’ll run it through our 11 trust signals — usually back in under a minute.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 flex-wrap justify-center">
                <Link
                  to="/scout"
                  data-testid="shop-search-submit-cta"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white font-semibold text-sm px-5 py-2 transition-colors"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Submit for scoring
                </Link>
                <Link to="/shop" className="rounded-full border border-slate-200 hover:border-slate-900 text-slate-900 font-semibold text-sm px-5 py-2 transition-colors">
                  Browse the catalog
                </Link>
              </div>
            </div>
          )}
        </>
      )}
    </ShopShell>
  );
};

export default ShopHome;
